import { format as formatDate } from 'date-fns'
import { Id } from 'types/definitions'
import DocumentStore from 'documents/DocumentStore'
import Document, { DocumentField } from 'documents/Document'
import DocumentVersionStore from './DocumentVersionStore'
import { assertValidFileType, assertValidFileSize } from 'utils/file-utils'
import { extractMessageFromError } from 'utils/error-util'
import DocumentTagStore from 'documents/DocumentTagStore'
import Workspace from 'iam/models/Workspace'
import DocumentVersion from 'documents/DocumentVersion'
import { DocumentCreationOpts, ZipFilesPayload } from './dtos/document'

export interface SuccessfulUploadDocRes {
  success: true
  file: File
  doc: Document
}

interface ErrorUploadDocRes {
  success: false
  file: File
  error: unknown
}

export type UploadDocResult = SuccessfulUploadDocRes | ErrorUploadDocRes

type SuccessResult<T> = {
  success: true
  value: T
}

type FailureResult = {
  success: false
  reason: Error
}

export type OperationResult<T> = SuccessResult<T> | FailureResult

export class DocumentActionsAdapter {
  constructor(
    private documentStore: DocumentStore,
    private documentVersionStore: DocumentVersionStore,
    private documentTagStore: DocumentTagStore
  ) {}

  tagsIn(workspace?: Workspace) {
    if (workspace) {
      return this.documentTagStore.tags.filter(
        (t) => t.workspaceId === workspace.id
      )
    }

    return this.documentTagStore.tags.items
  }

  downloadBlob(document: Document) {
    return this.documentStore.downloadBlob(document)
  }

  downloadDocument(document: Document, inline = false) {
    return this.documentStore.download(document, inline)
  }

  downloadAll(docs: ZipFilesPayload) {
    return this.documentStore.downloadAll(docs)
  }

  archiveDocument(document: Document) {
    return this.documentStore.archiveDocument(Id.assert(document.id))
  }

  async uploadNewDocument(
    file: File,
    creationOpts: DocumentCreationOpts
  ): Promise<UploadDocResult> {
    try {
      assertValidFileType(file)
      assertValidFileSize(file)

      const doc = await this.uploadDocumentAndVersion(file, creationOpts)
      return {
        success: true,
        file,
        doc,
      }
    } catch (err) {
      return {
        success: false,
        file,
        error: extractMessageFromError(err),
      }
    }
  }

  async correctFields(
    documentId: Id,
    documentVersionId: Id,
    fields: Array<DocumentField>
  ): Promise<void> {
    return this.documentVersionStore.correctFields(
      documentId,
      documentVersionId,
      {
        fields: fields.map((field) => ({
          id: field.id,
          file_id: field.fileId,
          path: field.path,
          value:
            field.type === 'DATE'
              ? formatDate(field.value, 'yyyy-MM-dd')
              : field.value,
        })),
      }
    )
  }

  private async uploadDocumentAndVersion(
    file: File,
    creationOpts: DocumentCreationOpts
  ): Promise<Document> {
    // Create the document
    const docRes = await this.tryCreateDocument(creationOpts, file.name)

    if (!docRes.success) {
      throw docRes.reason
    }
    const doc = docRes.value

    // Create the first version of the document
    const versionRes = await this.tryCreateVersion(doc, file)

    if (!versionRes.success) {
      throw versionRes.reason
    } else {
      return doc
    }
  }

  private async tryCreateDocument(
    creationOpts: DocumentCreationOpts,
    title: string
  ): Promise<OperationResult<Document>> {
    try {
      const doc = await this.documentStore.createDocument({
        title,
        ...creationOpts,
      })

      return {
        success: true,
        value: doc,
      }
    } catch (err) {
      return {
        success: false,
        reason: err,
      }
    }
  }

  private async tryCreateVersion(
    doc: Document,
    file: File
  ): Promise<OperationResult<DocumentVersion>> {
    try {
      const docVersion = await this.documentVersionStore.createVersion({
        action: { type: 'ReplaceContents', files: [file] },
        message: 'Initial version',
        document_id: Id.assert(doc.id),
      })
      return {
        success: true,
        value: docVersion,
      }
    } catch (err) {
      return {
        success: false,
        reason: err,
      }
    }
  }
}
